import {Bath, BedDouble, Heart, Maximize, Rotate3d, ShieldCheck, Scale} from 'lucide-react';
import {money, number} from '../lib/format';
import {useStore} from '../store/StoreContext';
import {coverPhoto, has360, pricePerM2, LISTING_TYPE, type Listing} from '../store/types';
import {Picture} from './Picture';

/**
 * One listing, as it appears in the market grid.
 *
 * The card answers the four questions a buyer asks before opening anything —
 * how much, how big, where, and can I look inside — and puts the two actions
 * that build a shortlist (save, compare) within reach without opening it.
 */
export function ListingCard({listing, onOpen}: {listing: Listing; onOpen: () => void}) {
  const {compare, saved, toggleCompare, toggleSaved} = useStore();
  const cover = coverPhoto(listing);
  const inCompare = compare.includes(listing.id);
  const isSaved = saved.includes(listing.id);
  const rent = listing.purpose === 'cho-thue';

  return (
    <article className="listing-card overflow-hidden rounded-card" data-picked={inCompare}>
      <button type="button" onClick={onOpen} className="block w-full text-left">
        <div className="relative">
          <Picture alt={listing.title} ratio="4 / 3" refItem={cover?.ref ?? null} />

          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            <span className="photo-tag rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide">
              {rent ? 'Cho thuê' : 'Bán'}
            </span>
            {listing.verified ? (
              <span className="photo-tag flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-bold">
                <ShieldCheck size={12} /> Đã xác minh
              </span>
            ) : null}
          </div>

          {has360(listing) ? (
            <span className="photo-tag absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold">
              <Rotate3d size={13} /> 360°
            </span>
          ) : null}

          {listing.status === 'da-ban' ? (
            <span className="absolute inset-0 flex items-center justify-center bg-[rgb(28_30_24/0.45)] text-[15px] font-bold uppercase tracking-widest text-white">
              Đã bán
            </span>
          ) : null}
        </div>

        <div className="p-4">
          <p className="text-[17px] font-extrabold leading-none text-accent">
            {money(listing.price)}
            {rent ? <span className="text-[12px] font-bold text-ink-3"> /tháng</span> : null}
          </p>
          <h3 className="mt-2 line-clamp-2 text-[14px] font-bold leading-snug text-ink">
            {listing.title}
          </h3>
          <p className="mt-1.5 truncate text-[12px] text-ink-3">
            {LISTING_TYPE[listing.type]} · {listing.district}, {listing.province}
          </p>

          <dl className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-ink-2">
            <div className="flex items-center gap-1.5">
              <Maximize size={14} className="text-ink-3" />
              <dd>{number(listing.area)} m²</dd>
            </div>
            {listing.bedrooms > 0 ? (
              <div className="flex items-center gap-1.5">
                <BedDouble size={14} className="text-ink-3" />
                <dd>{listing.bedrooms} PN</dd>
              </div>
            ) : null}
            {listing.bathrooms > 0 ? (
              <div className="flex items-center gap-1.5">
                <Bath size={14} className="text-ink-3" />
                <dd>{listing.bathrooms} WC</dd>
              </div>
            ) : null}
          </dl>

          <p className="mt-2 text-[11.5px] text-ink-3">
            {money(pricePerM2(listing))}
            /m²
          </p>
        </div>
      </button>

      <footer className="flex items-center gap-2 px-4 pb-4">
        <button
          type="button"
          aria-pressed={inCompare}
          onClick={() => toggleCompare(listing.id)}
          className={`flex min-h-11 flex-1 items-center justify-center gap-2 rounded-control px-3 text-[12.5px] font-semibold @xl:min-h-9 ${
            inCompare ? 'tile-active' : 'tile text-ink-2'
          }`}
        >
          <Scale size={15} />
          {inCompare ? 'Đang so sánh' : 'So sánh'}
        </button>
        <button
          type="button"
          aria-pressed={isSaved}
          aria-label={isSaved ? 'Bỏ lưu tin' : 'Lưu tin'}
          onClick={() => toggleSaved(listing.id)}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-control @xl:h-9 @xl:w-9 ${
            isSaved ? 'tile-active' : 'tile text-ink-2'
          }`}
        >
          <Heart size={16} fill={isSaved ? 'currentColor' : 'none'} />
        </button>
      </footer>
    </article>
  );
}
